import { Injectable } from '@angular/core';
import {API} from '@app/models';
import {TreeNode} from 'primeng/api';
import {ApiService} from './api.service';
import {Observable, forkJoin, from, map, of, shareReplay, finalize} from 'rxjs';
import { sortTreeNodes } from '@app/helpers/helper-functions';

@Injectable({
  providedIn: 'root'
})
export class UserSelectService {
  filteredusers: API.User[] = [];
  availableWorkplaces: any[] = [];
  availableWorkplacesFiltered: Map<number, any[]> = new Map();
  private workplacesInFlight$: Observable<any[]> | null = null;
  availableUtilities: any[] = [];
  availableWorkplacesIncludeUtilities: TreeNode[] = [];
  selectedWorkPlace: any;
  workplaces: TreeNode[] = [];
  workplacesLoading: boolean = false;
  users: API.User[] = [];
  selectedUser: any;
  usersLoading: boolean = false;
  userParams: string;

  constructor(
    private api: ApiService
  ) { }

  getWorkPlaces(clientId?: number): Observable<any[]> {
    // If no clientId, use the unfiltered cache
    if (!clientId) {
      if (this.availableWorkplaces.length !== 0) {
        return of(this.availableWorkplaces);
      }
      if (this.workplacesInFlight$) {
        return this.workplacesInFlight$;
      }
    } else {
      // If clientId is provided, check if it exists in the Map
      if (this.availableWorkplacesFiltered.has(clientId)) {
        return of(this.availableWorkplacesFiltered.get(clientId)!);
      }
    }

    // Build API params
    const params: any = { flat_list: false, simple: true };
    if (clientId) {
      params.client_id = clientId;
    }

    const request$ = from(this.api.getFromURL('/api/v1/groups', params)).pipe(
      map((wPlaces: any) => {
        const enrichNode = (node: any): any => {
          const enrichedNode = {
            ...node,
            data: { tag: node.tag },
            disabled: false
          };

          if (node.children && node.children.length > 0) {
            enrichedNode.children = node.children.map((child: any) => enrichNode(child));
          }

          return enrichedNode;
        };

        const workplaces = (wPlaces.groups || []).map((place: any) => enrichNode(place));

        // Store in appropriate cache
        if (!clientId) {
          this.availableWorkplaces = workplaces;
        } else {
          // Clear the map and store only the new clientId entry
          this.availableWorkplacesFiltered.clear();
          this.availableWorkplacesFiltered.set(clientId, workplaces);
        }

        return workplaces;
      }),
      finalize(() => { if (!clientId) this.workplacesInFlight$ = null; }),
      shareReplay(1)
    );

    if (!clientId) {
      this.workplacesInFlight$ = request$;
    }

    return request$;
  }

  getUtilities(): Observable<any[]> {
    if(this.availableUtilities.length!==0){
      return of(this.availableUtilities);
    }
    return from(this.api.getFromURL('/api/v1/utilities',{flat_list:true, simple: true})).pipe(
      map((resp: any) => {
        this.availableUtilities = resp.utilities
        return this.availableUtilities;
      })
    );
  }

  getWorkplacesIncludeUtilities(): Observable<TreeNode[]> {
    if (this.availableWorkplacesIncludeUtilities.length !== 0) {
      return of(this.availableWorkplacesIncludeUtilities);
    }

    return forkJoin([this.getWorkPlaces(), this.getUtilities()]).pipe(
      map(([workplaces, utilities]) => {
        const addUtilitiesToNodes = (nodes: any[]): TreeNode[] => {
          return nodes.map(node => {
            const children = node.children ? addUtilitiesToNodes(node.children) : [];
            const matchingUtilities = utilities
              .filter(util => util.group_id === node.id)
              .map(util => ({
                key: `u-${util.id}`,
                label: util.name,
                data: {tag: 'utility'}
              }));

            return {
              label: node.name,
              key: node.id,
              data: {tag: node.tag},
              children: [...children, ...matchingUtilities]
            };
          });
        };

        this.availableWorkplacesIncludeUtilities = addUtilitiesToNodes(workplaces);
        sortTreeNodes(this.availableWorkplacesIncludeUtilities)
        return this.availableWorkplacesIncludeUtilities;
      })
    );
  }

  getUsers(short:boolean=true, scope:string='admin', roles: string[]=['client_admin','manager','checker'], usersUrl: string = '/users.json' ): Observable<any[]> {
    let params = `short=${short}`;
    if (scope) {
      params += `&scope=${scope}`;
    }
    for(let role of roles){
      params+='&roles[]='+role;
    }
    if(this.userParams == params && this.filteredusers.length > 0){
      return of(this.filteredusers);
    }
    this.userParams = params;
    
    this.usersLoading = true;  
    return from(this.api.getFromURL(`${usersUrl}?${this.userParams}`)).pipe(
      map((users: any[]) => {
        this.filteredusers = this.users = users.map((user: any) => {
          return {
            ...user,
            id: user.id,
            name: user.full_name,
          };
        })
        // .filter((user: any) => {
        //   if (Array.isArray(user.roles)) {
        //     return !user.roles.map((role: any) => role.name).includes('external_worker');
        //   } else {
        //     return true;
        //   }
        // });
        this.usersLoading = false;
        return this.filteredusers; // Return the filtered users
      })
    );
  }

  /**
   * Clears all cached data in the service.
   * Should be called during logout to prevent data leakage between sessions.
   */
  clearCache(): void {
    this.filteredusers = [];
    this.availableWorkplaces = [];
    this.availableWorkplacesFiltered.clear();
    this.availableUtilities = [];
    this.availableWorkplacesIncludeUtilities = [];
    this.selectedWorkPlace = null;
    this.workplaces = [];
    this.workplacesLoading = false;
    this.users = [];
    this.selectedUser = null;
    this.usersLoading = false;
    this.userParams = '';
    this.workplacesInFlight$ = null;
  }
  
}
